import { useState } from "react";
import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import { App } from "antd5";
import { AxiosError } from "axios";
import pLimit from "p-limit";

import { MAX_PARALLEL_DOCUMENT_UPLOADS, useVariableValue } from "lib/featureFlags";
import { useDeleteBid } from "../bids/useDeleteBid";
import { useCreateBidDocument } from "./useCreateBidDocument";
import { useUploadNoticeDocument } from "./useUploadNoticeDocument";

export type FileUploadStatus = {
  file: File;
  isError: boolean;
  isUploaded: boolean;
  isProcessed: boolean;
  documentId?: string;
  uploadProgress?: number;
};

type MutationParams = {
  bidId: string;
};

type UploadFilesInput = {
  files: File[];
  onComplete?: (bidId: string) => void;
};

export function useUploadBidDocuments({ files, onComplete }: UploadFilesInput) {
  const { message } = App.useApp();
  const [uploadStatus, setUploadStatus] = useState<FileUploadStatus[]>([]);

  /**
   * Flexible utility function which updates the uploadStatus of a file in
   * the upload queue
   */
  const updateFileStatus = (fileName: string, updates: Partial<FileUploadStatus>) => {
    setUploadStatus((prev) =>
      prev.map((fileStatus) =>
        fileStatus.file.name === fileName ? { ...fileStatus, ...updates } : fileStatus,
      ),
    );
  };

  /**
   * Triggers if one file in the list fails to upload or
   * process. Resets the upload status of all the files and
   * allows the user to restart. Deletes the bid and the
   * already uploaded/processed documents too
   * @param error
   */
  const onUploadError = async (bidId: string, error: unknown) => {
    Sentry.captureException(error);

    message.error(`Error processing file(s). Please try again`);

    // Reset the upload status of all files,
    // except the errored one
    setUploadStatus((prev) =>
      prev.map((fileStatus) => {
        if (fileStatus.isError) {
          return fileStatus;
        }

        return {
          ...fileStatus,
          isError: false,
          isUploaded: false,
          isProcessed: false,
          documentId: undefined,
          uploadProgress: undefined,
        };
      }),
    );

    await deleteBid({ input: { bidId } });
  };

  const { mutateAsync: createBidDocument } = useCreateBidDocument();
  const { mutateAsync: uploadNoticeDocument } = useUploadNoticeDocument();
  const { mutateAsync: deleteBid } = useDeleteBid();

  const maxParallelDocumentUploads = useVariableValue(MAX_PARALLEL_DOCUMENT_UPLOADS, 1);

  const uploadFiles = useMutation<void, AxiosError, MutationParams>({
    mutationFn: async ({ bidId }) => {
      const limit = pLimit(maxParallelDocumentUploads);

      // This flag will be true if any of the files failed to upload
      let hasFailed = false;

      const processFile = async (file: File) => {
        try {
          const { documentId } = await uploadNoticeDocument({
            file,
            bidId,
            onProgress: (progress) => {
              updateFileStatus(file.name, {
                uploadProgress: progress,
                isUploaded: progress >= 100,
              });
            },
          });

          // Here the file is processed & indexed
          await createBidDocument({
            request: {
              bidId,
              fileName: file.name,
              documentId,
            },
          });

          // If any document has failed, don't say docs are processed
          if (!hasFailed) {
            updateFileStatus(file.name, { isProcessed: true });
          }
        } catch (error) {
          updateFileStatus(file.name, { isError: true });
          hasFailed = true;
          // Throw error up the Promise.all chain
          throw error;
        }
      };

      try {
        await Promise.all(files.map((file) => limit(() => processFile(file))));
      } catch (error) {
        await onUploadError(bidId, error);

        return;
      }

      if (onComplete) {
        await onComplete(bidId);
      }
    },
  });

  return {
    ...uploadFiles,
    uploadStatus,
    setUploadStatus,
  };
}
